<template lang="pug">
.editor-canvas(:style="boxStyle" ref="canvas")
    //- Canvas-expand fill preview: behind the image, sized to the working box. When the
    //- crop frame extends past the (zoomed-out) image, the revealed area shows this fill —
    //- a solid colour, or a checkerboard for transparent / AI. Visible only while expanding.
    .editor-canvas__fill(v-if="expanding" :class="{ 'editor-canvas__fill--checker': !fillColor }" :style="fillStyle")
    //- The stage holds the image AND the crop overlay and carries ONE CSS transform
    //- (rotate + flip), so they transform together — the crop box tracks the motive
    //- automatically and there is no per-tool coordinate math. The crop rectangle lives
    //- in source (un-transformed) coordinates = our focal-point model. Scrolling out shrinks
    //- the stage within the fixed box, revealing the fill area the crop can be dragged over.
    .editor-canvas__stage(:style="stageStyle")
        img.editor-canvas__image(:src="imageSrc" draggable="false" @load="onImageLoad" @mousedown.prevent="mode === 'crop' && allowExpand && startImagePan($event)")
        //- The crop area is always shown: editable + prominent in crop mode (shaded
        //- outside, grid, handles); a subtle, non-interactive outline otherwise so the
        //- selected area stays visible without the heavy tool chrome.
        .editor-canvas__crop(
            v-if="cropRectStyle"
            :class="{ 'editor-canvas__crop--passive': mode !== 'crop' }"
            :style="cropRectStyle"
            @mousedown.prevent.stop="mode === 'crop' && startMove($event)"
        )
            template(v-if="mode === 'crop'")
                .editor-canvas__grid
                .editor-canvas__handle(
                    v-for="handle in handles"
                    :key="handle"
                    :class="`editor-canvas__handle--${handle}`"
                    :style="{ cursor: handleCursor(handle) }"
                    @mousedown.prevent.stop="event => startResize(event, handle)"
                )
</template>

<script>
import _ from 'lodash';

// A self-contained image-edit canvas: shows the image with the active transform and a
// draggable/resizable crop overlay. No cropper.js — we own every coordinate, so rotate/flip
// simply transform the stage and the crop follows along. Scrolling out (allowExpand) zooms
// the view below 1×, revealing the fill area the crop can be dragged over (canvas-expand).
export default {
    name: 'EditorCanvas',
    props: {
        // Uncropped base URL (crop mode) and the baked-result URL (view mode).
        cropSource: { type: String, default: null },
        displaySource: { type: String, default: null },
        mode: { type: String, default: null }, // 'crop' | null
        // Natural pixel size of the crop source.
        naturalWidth: { type: Number, default: 0 },
        naturalHeight: { type: Number, default: 0 },
        transform: { type: Object, default: () => ({ rotate: 0, flipH: false, flipV: false }) },
        // Crop rectangle in SOURCE pixels: { x, y, width, height }. null = whole image.
        // May extend BEYOND [0..natural] when the canvas is expanded (the out-of-bounds
        // area is filled on save).
        crop: { type: Object, default: null },
        aspectRatio: { type: Number, default: null }, // null/NaN = free
        availableWidth: { type: Number, default: 520 },
        maxHeight: { type: Number, default: 640 },
        padding: { type: Number, default: 16 },
        // Canvas-expand: enables scroll-to-zoom-out + dragging the crop past the image edge.
        allowExpand: { type: Boolean, default: false },
        // CSS colour for the revealed-area fill preview (null → checkerboard for transparent/AI).
        fillColor: { type: String, default: null },
    },
    data() {
        return {
            drag: null, // { kind: 'move'|'resize'|'pan', handle, startX, startY, startCrop }
            zoom: 1,    // view zoom (≤ 1). < 1 shrinks the image, revealing the fill area.
        };
    },
    computed: {
        // Always show the uncropped base (transformed via the stage). The crop is shown
        // as an overlay, so the canvas keeps the source aspect in every mode (no stretched
        // baked result) and the selected area stays visible when the tool is off.
        imageSrc() {
            return this.cropSource || this.displaySource;
        },
        rotate() {
            return ((Number(_.get(this.transform, 'rotate', 0)) % 360) + 360) % 360;
        },
        rotated() {
            return this.rotate === 90 || this.rotate === 270;
        },
        flipH() { return !!_.get(this.transform, 'flipH'); },
        flipV() { return !!_.get(this.transform, 'flipV'); },
        // The image's bounding box in DISPLAYED (post-rotation) source pixels.
        displayedImageWidth() { return this.rotated ? (this.naturalHeight || 1) : (this.naturalWidth || 1); },
        displayedImageHeight() { return this.rotated ? (this.naturalWidth || 1) : (this.naturalHeight || 1); },
        // The crop frame extends beyond the image on some side → there is fill to show.
        cropExceedsImage() {
            if (!this.crop) return false;
            return this.crop.x < -0.5 || this.crop.y < -0.5
                || (this.crop.x + this.crop.width) > this.naturalWidth + 0.5
                || (this.crop.y + this.crop.height) > this.naturalHeight + 0.5;
        },
        // Expanding = zoomed out, or the crop already reaches past the image.
        expanding() {
            return this.allowExpand && (this.zoom < 0.999 || this.cropExceedsImage);
        },
        // The working box is FIXED at the zoom-1 image-fit size — it does NOT grow with the
        // crop. Zooming out shrinks the image inside it, which is what reveals the fill area.
        boxDimensions() {
            const width = this.displayedImageWidth;
            const height = this.displayedImageHeight;
            const aspect = width / height;
            const paneWidth = (this.availableWidth || 520) - 2 * this.padding;
            let boxWidth = paneWidth;
            let boxHeight = boxWidth / aspect;
            if (boxHeight > this.maxHeight) {
                boxHeight = this.maxHeight;
                boxWidth = boxHeight * aspect;
            }
            return { width: Math.round(boxWidth), height: Math.round(boxHeight) };
        },
        // Uniform scale mapping source pixels → stage pixels: the zoom-1 fit times the view
        // zoom. At zoom 1 the image fills the box (unchanged); below 1 it shrinks.
        baseFit() {
            const box = this.boxDimensions;
            return this.rotated
                ? Math.min(box.width / this.naturalHeight, box.height / this.naturalWidth)
                : Math.min(box.width / this.naturalWidth, box.height / this.naturalHeight);
        },
        // The zoom applies in EVERY mode. It is not a crop-tool nicety: an expanded crop
        // (frame larger than the image) only fits the working box because the view is
        // zoomed out to maxZoom. Tying it to allowExpand made leaving the crop tool snap
        // the image back to 100% while the oversized crop overlay kept its size, so the
        // frame spilled outside the canvas. allowExpand gates INTERACTION (scroll, pan,
        // dragging past the edge), never the rendering scale.
        scale() {
            return this.baseFit * this.zoom;
        },
        stageWidth() { return (this.naturalWidth || 1) * this.scale; },
        stageHeight() { return (this.naturalHeight || 1) * this.scale; },
        boxStyle() {
            return { width: this.boxDimensions.width + 'px', height: this.boxDimensions.height + 'px' };
        },
        fillStyle() {
            return {
                width: this.boxDimensions.width + 'px',
                height: this.boxDimensions.height + 'px',
                background: this.fillColor || undefined,
            };
        },
        stageStyle() {
            const box = this.boxDimensions;
            const left = (box.width - this.stageWidth) / 2;
            const top = (box.height - this.stageHeight) / 2;
            const scaleX = this.flipH ? -1 : 1;
            const scaleY = this.flipV ? -1 : 1;
            return {
                width: this.stageWidth + 'px',
                height: this.stageHeight + 'px',
                left: left + 'px',
                top: top + 'px',
                transform: `rotate(${this.rotate}deg) scaleX(${scaleX}) scaleY(${scaleY})`,
            };
        },
        // The crop rectangle in stage pixels (source px × scale). null → no overlay.
        cropRectStyle() {
            if (!this.crop) return null;
            return {
                left: (this.crop.x * this.scale) + 'px',
                top: (this.crop.y * this.scale) + 'px',
                width: (this.crop.width * this.scale) + 'px',
                height: (this.crop.height * this.scale) + 'px',
            };
        },
        handles() {
            return ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
        },
        lockedAspect() {
            const ratio = Number(this.aspectRatio);
            return (ratio && !Number.isNaN(ratio)) ? ratio : null;
        },
        // The largest zoom (≤1) at which the current crop frame still fits the working box,
        // centred on the image. Capping zoom-IN to this stops an out-of-bounds crop from
        // overflowing the canvas when the user scrolls back in. (The box's source half-extent
        // at zoom z is natW/2/z on each axis — same in both orientations, see clampBounds.)
        maxZoom() {
            if (!this.crop || !this.naturalWidth || !this.naturalHeight) return 1;
            const centerX = this.naturalWidth / 2;
            const centerY = this.naturalHeight / 2;
            const reachX = Math.max(centerX, centerX - this.crop.x, (this.crop.x + this.crop.width) - centerX);
            const reachY = Math.max(centerY, centerY - this.crop.y, (this.crop.y + this.crop.height) - centerY);
            return Math.min(1, centerX / reachX, centerY / reachY);
        },
        // The bounds (source px) the crop may roam within. Without expand it's the image
        // (unchanged). With expand it's the working box mapped to source coords, centred on
        // the image — so at zoom 1 it equals the image and zooming out opens symmetric room.
        // Rotation-aware: the box's screen width maps to source-Y when rotated 90/270.
        clampBounds() {
            if (!this.allowExpand) {
                return { minX: 0, minY: 0, maxX: this.naturalWidth, maxY: this.naturalHeight };
            }
            const box = this.boxDimensions;
            const sourceRangeX = (this.rotated ? box.height : box.width) / this.scale;
            const sourceRangeY = (this.rotated ? box.width : box.height) / this.scale;
            const centerX = this.naturalWidth / 2;
            const centerY = this.naturalHeight / 2;
            return {
                minX: centerX - sourceRangeX / 2,
                maxX: centerX + sourceRangeX / 2,
                minY: centerY - sourceRangeY / 2,
                maxY: centerY + sourceRangeY / 2,
            };
        },
    },
    watch: {
        // Keep the zoom within the range where the crop fits the box: if the crop grew
        // (dragged past the edge), zoom out so it stays visible. View-only.
        crop: {
            immediate: true,
            handler() {
                // Not during an active drag — the clamp bounds already keep the crop inside
                // the box while dragging; this is for restore-on-open of an expanded crop.
                if (!this.drag && this.zoom > this.maxZoom) {
                    this.$nextTick(() => { this.zoom = this.maxZoom; });
                }
            },
        },
    },
    methods: {
        onImageLoad(event) {
            // Report the image's TRUE intrinsic size. Stored variant dims can be a folded
            // crop's output (≠ the uncropped pixels the cropper actually edits), which would
            // size the stage to the wrong aspect and skew the image. The loaded <img> is the
            // source of truth, so the parent uses these for naturalCropSize.
            const img = event && event.target;
            if (img && img.naturalWidth && img.naturalHeight) {
                this.$emit('ready', { width: img.naturalWidth, height: img.naturalHeight });
            } else {
                this.$emit('ready');
            }
        },
        // The resize cursor must match the handle's VISUAL edge, which moves when the
        // stage is rotated/flipped. Transform the handle's direction vector by the flips
        // then the rotation, and classify the result into a resize cursor.
        handleCursor(handle) {
            const directions = {
                n: [0, -1], s: [0, 1], e: [1, 0], w: [-1, 0],
                nw: [-1, -1], ne: [1, -1], se: [1, 1], sw: [-1, 1],
            };
            let [dx, dy] = directions[handle] || [0, 0];
            if (this.flipH) dx = -dx;
            if (this.flipV) dy = -dy;
            let tx = dx;
            let ty = dy;
            // CSS rotate() is clockwise (screen coords): (x,y) → (-y, x) per 90°.
            if (this.rotate === 90) { tx = -dy; ty = dx; }
            else if (this.rotate === 180) { tx = -dx; ty = -dy; }
            else if (this.rotate === 270) { tx = dy; ty = -dx; }
            if (ty === 0) return 'ew-resize';
            if (tx === 0) return 'ns-resize';
            return (tx * ty > 0) ? 'nwse-resize' : 'nesw-resize';
        },
        // Convert a SCREEN delta into a SOURCE-pixel delta by undoing the stage's CSS
        // transform (inverse rotate, then inverse flip, then unscale). This keeps dragging
        // intuitive (follows the cursor) while storing the crop in source coordinates.
        screenDeltaToSource(screenDeltaX, screenDeltaY) {
            let localX;
            let localY;
            switch (this.rotate) {
                case 90: localX = screenDeltaY; localY = -screenDeltaX; break;
                case 180: localX = -screenDeltaX; localY = -screenDeltaY; break;
                case 270: localX = -screenDeltaY; localY = screenDeltaX; break;
                default: localX = screenDeltaX; localY = screenDeltaY;
            }
            if (this.flipH) localX = -localX;
            if (this.flipV) localY = -localY;
            return { x: localX / this.scale, y: localY / this.scale };
        },
        startMove(event) {
            this.beginDrag(event, { kind: 'move' });
        },
        startResize(event, handle) {
            this.beginDrag(event, { kind: 'resize', handle });
        },
        // Dragging the IMAGE (not the crop) pans it under the frame — i.e. moves the crop in
        // the opposite direction. Same single relative offset as moving the crop, so it stays
        // WYSIWYG. Only meaningful once there is room (expand).
        startImagePan(event) {
            this.beginDrag(event, { kind: 'pan' });
        },
        beginDrag(event, info) {
            this.drag = {
                ...info,
                startX: event.clientX,
                startY: event.clientY,
                startCrop: { ...this.crop },
            };
            window.addEventListener('mousemove', this.onDragMove);
            window.addEventListener('mouseup', this.endDrag);
        },
        onDragMove(event) {
            if (!this.drag) return;
            const delta = this.screenDeltaToSource(event.clientX - this.drag.startX, event.clientY - this.drag.startY);
            if (this.drag.kind === 'move') this.applyMove(delta);
            else if (this.drag.kind === 'pan') this.applyMove({ x: -delta.x, y: -delta.y });
            // Modifiers (read live so holding/releasing mid-drag updates immediately):
            // Shift = constrain aspect ratio, Alt/Option = resize from the centre.
            else this.applyResize(delta, { symmetric: event.altKey, constrainRatio: event.shiftKey });
        },
        applyMove(delta) {
            const start = this.drag.startCrop;
            const bounds = this.clampBounds;
            const x = this.clamp(start.x + delta.x, bounds.minX, bounds.maxX - start.width);
            const y = this.clamp(start.y + delta.y, bounds.minY, bounds.maxY - start.height);
            this.emitCrop({ x, y, width: start.width, height: start.height });
        },
        applyResize(delta, modifiers = {}) {
            const start = this.drag.startCrop;
            const handle = this.drag.handle;
            const bounds = this.clampBounds;
            const minSize = 24;
            const horizontal = handle.indexOf('e') !== -1 || handle.indexOf('w') !== -1;
            const vertical = handle.indexOf('n') !== -1 || handle.indexOf('s') !== -1;
            // Aspect held during this drag: a locked preset always wins; otherwise Shift
            // locks proportions to the crop's STARTING aspect.
            const aspect = this.lockedAspect
                || (modifiers.constrainRatio ? (start.width / start.height) : null);

            // Alt/Option: resize from the fixed centre — the grabbed edge mirrors to the
            // opposite edge so the box grows/shrinks symmetrically in all directions.
            if (modifiers.symmetric) {
                const centerX = start.x + start.width / 2;
                const centerY = start.y + start.height / 2;
                let width = start.width;
                let height = start.height;
                if (handle.indexOf('w') !== -1) width = start.width - 2 * delta.x;
                if (handle.indexOf('e') !== -1) width = start.width + 2 * delta.x;
                if (handle.indexOf('n') !== -1) height = start.height - 2 * delta.y;
                if (handle.indexOf('s') !== -1) height = start.height + 2 * delta.y;
                width = Math.max(width, minSize);
                height = Math.max(height, minSize);
                if (aspect) {
                    if (vertical && !horizontal) width = height * aspect;
                    else height = width / aspect;
                }
                // Clamp symmetrically: max extent is twice the distance to the nearest bound.
                const maxWidth = 2 * Math.min(centerX - bounds.minX, bounds.maxX - centerX);
                const maxHeight = 2 * Math.min(centerY - bounds.minY, bounds.maxY - centerY);
                if (width > maxWidth) { width = maxWidth; if (aspect) height = width / aspect; }
                if (height > maxHeight) { height = maxHeight; if (aspect) width = height * aspect; }
                this.emitCrop({ x: centerX - width / 2, y: centerY - height / 2, width, height });
                return;
            }

            // Default: anchor at the opposite edge/corner.
            let { x, y, width, height } = start;
            const right = start.x + start.width;
            const bottom = start.y + start.height;
            if (handle.indexOf('w') !== -1) {
                x = this.clamp(start.x + delta.x, bounds.minX, right - minSize);
                width = right - x;
            }
            if (handle.indexOf('e') !== -1) {
                width = this.clamp(start.width + delta.x, minSize, bounds.maxX - start.x);
            }
            if (handle.indexOf('n') !== -1) {
                y = this.clamp(start.y + delta.y, bounds.minY, bottom - minSize);
                height = bottom - y;
            }
            if (handle.indexOf('s') !== -1) {
                height = this.clamp(start.height + delta.y, minSize, bounds.maxY - start.y);
            }
            if (aspect) {
                const adjusted = this.constrainAspect(handle, { x, y, width, height }, right, bottom, aspect);
                x = adjusted.x; y = adjusted.y; width = adjusted.width; height = adjusted.height;
            }
            this.emitCrop({ x, y, width, height });
        },
        // Keep the locked aspect by deriving the dependent dimension from the grabbed edge.
        // CORNER handles anchor the opposite corner. SIDE (edge) handles move only their own
        // edge along its axis (opposite edge fixed) and grow the PERPENDICULAR dimension
        // SYMMETRICALLY about the crop's centre — so e.g. dragging the top edge taller widens
        // the crop evenly on the left AND right, keeping it centred. Clamps proportionally so
        // a bound can't stretch one side and break the ratio.
        constrainAspect(handle, rect, right, bottom, aspect = this.lockedAspect) {
            const hasW = handle.indexOf('w') !== -1;
            const hasE = handle.indexOf('e') !== -1;
            const hasN = handle.indexOf('n') !== -1;
            const hasS = handle.indexOf('s') !== -1;
            const horizontal = hasW || hasE;
            const vertical = hasN || hasS;
            const bounds = this.clampBounds;
            const start = this.drag.startCrop;

            // Side handle on the vertical axis (n/s): height is primary, width is derived.
            // The derived width is centred horizontally WHEN there is room on both sides, but
            // shifts into the available space when one side hits the boundary (so the resize
            // keeps working instead of locking up); it is only truly capped at the total
            // horizontal room.
            if (vertical && !horizontal) {
                const centerX = start.x + start.width / 2;
                const maxHeightAxis = hasN ? (bottom - bounds.minY) : (bounds.maxY - rect.y);
                let height = Math.min(rect.height, maxHeightAxis);
                let width = height * aspect;
                const totalWidth = bounds.maxX - bounds.minX;
                if (width > totalWidth) { width = totalWidth; height = width / aspect; }
                const x = this.clamp(centerX - width / 2, bounds.minX, bounds.maxX - width);
                return { x, y: hasN ? (bottom - height) : rect.y, width, height };
            }

            // Side handle on the horizontal axis (e/w): width is primary, height is derived,
            // centred vertically when possible and shifted into the available space when a
            // boundary blocks one side (capped only at the total vertical room).
            if (horizontal && !vertical) {
                const centerY = start.y + start.height / 2;
                const maxWidthAxis = hasW ? (right - bounds.minX) : (bounds.maxX - rect.x);
                let width = Math.min(rect.width, maxWidthAxis);
                let height = width / aspect;
                const totalHeight = bounds.maxY - bounds.minY;
                if (height > totalHeight) { height = totalHeight; width = height * aspect; }
                const y = this.clamp(centerY - height / 2, bounds.minY, bounds.maxY - height);
                return { x: hasW ? (right - width) : rect.x, y, width, height };
            }

            // Corner handle: derive height from width, anchored at the opposite corner.
            let width = rect.width;
            const maxWidth = hasW ? (right - bounds.minX) : (bounds.maxX - rect.x);
            const maxHeight = hasN ? (bottom - bounds.minY) : (bounds.maxY - rect.y);
            width = Math.min(width, maxWidth, maxHeight * aspect);
            const height = width / aspect;
            return { x: hasW ? (right - width) : rect.x, y: hasN ? (bottom - height) : rect.y, width, height };
        },
        emitCrop(rect) {
            this.$emit('update:crop', {
                x: Math.round(rect.x),
                y: Math.round(rect.y),
                width: Math.round(rect.width),
                height: Math.round(rect.height),
            });
        },
        endDrag() {
            this.drag = null;
            window.removeEventListener('mousemove', this.onDragMove);
            window.removeEventListener('mouseup', this.endDrag);
            this.$emit('crop-end');
        },
        // Mouse wheel zooms the VIEW out/in (canvas-expand). Scrolling out shrinks the image
        // within the fixed box, revealing the fill area; scrolling in returns toward 1×.
        // View-only — it never changes the saved crop. Left to the page when expand is off.
        onWheel(event) {
            if (!this.allowExpand || this.mode !== 'crop') return;
            event.preventDefault();
            const step = event.deltaY > 0 ? 0.92 : (1 / 0.92);
            // Cap zoom-IN at maxZoom so an out-of-bounds crop never overflows the canvas.
            this.zoom = Math.max(0.15, Math.min(this.maxZoom, this.zoom * step));
        },
        clamp(value, min, max) {
            return Math.max(min, Math.min(max, value));
        },
    },
    mounted() {
        // Attach the wheel listener explicitly as NON-passive so preventDefault() actually
        // stops the page from scrolling while zooming the canvas (a template @wheel binding
        // can end up passive, letting the page scroll instead of zooming).
        if (this.$refs.canvas) {
            this.$refs.canvas.addEventListener('wheel', this.onWheel, { passive: false });
        }
    },
    beforeDestroy() {
        window.removeEventListener('mousemove', this.onDragMove);
        window.removeEventListener('mouseup', this.endDrag);
        if (this.$refs.canvas) {
            this.$refs.canvas.removeEventListener('wheel', this.onWheel);
        }
    },
};
</script>

<style lang="stylus" scoped>
.editor-canvas
    position relative
    margin 0 auto

.editor-canvas__fill
    position absolute
    top 0
    left 0
    box-shadow inset 0 0 0 1px rgba(0, 0, 0, 0.12)

    // Transparent / AI fill → a checkerboard so the user can tell it apart from a white fill.
    &--checker
        background-color #fff
        background-image linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)
        background-size 16px 16px
        background-position 0 0, 0 8px, 8px -8px, -8px 0px

.editor-canvas__stage
    position absolute
    transform-origin center center

.editor-canvas__image
    width 100%
    height 100%
    display block
    user-select none
    -webkit-user-drag none

.editor-canvas__crop
    position absolute
    box-shadow 0 0 0 9999px rgba(0, 0, 0, 0.45)
    outline 1px solid rgba(255, 255, 255, 0.9)
    cursor move

    // Tool off: keep the crop area clearly visible but without the editing chrome (no
    // handles / grid). A white outline + a dark inset line stays legible on any image,
    // and a moderate dim outside makes the selected area pop.
    &--passive
        box-shadow 0 0 0 9999px rgba(0, 0, 0, 0.38), inset 0 0 0 1px rgba(0, 0, 0, 0.45)
        outline 1px solid rgba(255, 255, 255, 0.95)
        cursor default
        pointer-events none

.editor-canvas__grid
    position absolute
    top 0
    left 0
    right 0
    bottom 0
    background-image linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)
    background-size 33.33% 33.33%
    background-position -1px -1px
    pointer-events none

.editor-canvas__handle
    position absolute
    width 12px
    height 12px
    background #fff
    border 1px solid rgba(0, 0, 0, 0.35)
    border-radius 2px

    &--nw
        top -6px
        left -6px
        cursor nwse-resize
    &--n
        top -6px
        left calc(50% - 6px)
        cursor ns-resize
    &--ne
        top -6px
        right -6px
        cursor nesw-resize
    &--e
        top calc(50% - 6px)
        right -6px
        cursor ew-resize
    &--se
        bottom -6px
        right -6px
        cursor nwse-resize
    &--s
        bottom -6px
        left calc(50% - 6px)
        cursor ns-resize
    &--sw
        bottom -6px
        left -6px
        cursor nesw-resize
    &--w
        top calc(50% - 6px)
        left -6px
        cursor ew-resize
</style>
